/**
 * Normalizes filter selection values to an array of primitives
 * Handles both direct values and objects with a 'value' property
 */
export function normalizeSelectionValues(raw: unknown): unknown[] {
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.map((item) => {
    if (item && typeof item === "object") {
      const record = item as Record<string, unknown>;
      if ("value" in record) return record.value;
    }
    return item;
  });
}

/**
 * Normalizes a filter value to a number
 * Handles direct numbers and objects with 'value' or 'max' properties
 */
export function normalizeNumber(raw: unknown): number | undefined {
  if (typeof raw === "number") return raw;
  if (raw && typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    const value = record["value"];
    if (typeof value === "number") return value;
    const max = record["max"];
    if (typeof max === "number") return max;
  }
  return undefined;
}

/**
 * Formats bedroom count into a human-readable string
 * @param selectedBedrooms - Raw bedroom filter values
 * @returns Formatted string like "studio", "2 beds", "studio - 3+ beds", etc.
 */
export function formatBedroomsText(selectedBedrooms: unknown): string {
  const beds = normalizeSelectionValues(selectedBedrooms);
  if (beds.length === 0) return "apartments";

  const sorted = [...beds]
    .map((v) => (typeof v === "number" ? v : NaN))
    .filter((n): n is number => Number.isFinite(n))
    .sort((a, b) => a - b);

  if (sorted.length === 0) return "apartments";

  if (sorted.length === 1) {
    const b = sorted[0]!;
    if (b === 0) return "studio";
    if (b >= 3) return `${b}+ beds`;
    return `${b} bed${b === 1 ? "" : "s"}`;
  }

  const min = sorted[0]!;
  const max: number = sorted[sorted.length - 1]!;
  const minLabel = min === 0 ? "studio" : `${min} bed${min === 1 ? "" : "s"}`;
  const maxLabel = max >= 3 ? `${max}+ beds` : `${max} beds`;
  return `${minLabel} or ${maxLabel}`;
}
